import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useWatch, type UseFormReturn } from 'react-hook-form';

import type { ReceiveConvoyContextDto, ReceiveConvoyLocationDto } from '@/entities/receive';
import {
  CONFIRM_ARRIVAL_UNIT_MODE,
  ConfirmArrivalHandlingSummaryBanner,
  ConfirmArrivalUnitToolbar,
  summarizeHandlingUnits,
  supportsHandlingUnits,
  useSecondaryUnitCells,
  type ConfirmArrivalUnitMode
} from '@/features/receive-confirm-arrival';
import { DataTable } from '@/shared/ui/data-table';
import type { SelectOption } from '@/shared/ui/filters';

import { buildConvoyTableRows, type ReceiveConvoyTableRow } from '../lib/table-rows';
import type { ReceiveConvoyFormValues } from '../model/types';
import { getReceiveConvoyColumns } from './receive-convoy-columns';
import { ReceiveConvoyTruckDocuments, ReceiveConvoyTruckHeader } from './receive-convoy-truck-rows';

interface ReceiveConvoyLinesTableProps {
  form: UseFormReturn<ReceiveConvoyFormValues>;
  context: ReceiveConvoyContextDto;
  locationsById: Map<string, ReceiveConvoyLocationDto>;
  unitMode: ConfirmArrivalUnitMode;
  onUnitModeChange: (mode: ConfirmArrivalUnitMode) => void;
  onResetQuantities: () => void;
}

/**
 * The legacy "Handling quantity summary" across the whole convoy. Its own
 * component because it is the one thing that watches every truck's quantities.
 */
function ReceiveConvoyHandlingSummary({ form, context }: Pick<ReceiveConvoyLinesTableProps, 'form' | 'context'>) {
  const shipments = useWatch({ control: form.control, name: 'shipments' });

  const summary = useMemo(() => {
    const linesById = new Map(
      context.shipments.flatMap((shipment) => shipment.lines).map((line) => [line.lineId, line])
    );
    return summarizeHandlingUnits(
      (shipments ?? []).flatMap((shipment) =>
        shipment.lines.flatMap((entered) => {
          const line = linesById.get(entered.lineId);
          return line ? [{ line, inventoryQty: entered.goodQty + entered.damagedQty + entered.lostQty }] : [];
        })
      )
    );
  }, [context.shipments, shipments]);

  return <ConfirmArrivalHandlingSummaryBanner summary={summary} />;
}

const isGroupRow = (row: ReceiveConvoyTableRow) => row.kind !== 'line';

// The documents row belongs to the lines above it, so it drops the group look.
const rowClassName = (row: ReceiveConvoyTableRow) =>
  row.kind === 'documents' ? 'bg-transparent hover:bg-transparent' : '';

/**
 * Every truck of the convoy in the shared `DataTable`, as a flat run of rows per
 * truck: header, consignment lines, documents. The header and documents rows are
 * the table's full-width group rows.
 */
export function ReceiveConvoyLinesTable({
  form,
  context,
  locationsById,
  unitMode,
  onUnitModeChange,
  onResetQuantities
}: ReceiveConvoyLinesTableProps) {
  const { t } = useTranslation();
  const { control, setValue } = form;

  const showLostColumn = context.shipments.some((shipment) => shipment.lostQtyAllowed);
  const anyBundleLines = context.shipments.some((shipment) => shipment.lines.some(supportsHandlingUnits));
  const { isSecondaryOpen, toggleSecondary, anySecondaryOpen } = useSecondaryUnitCells();
  const showsHandlingFigures = unitMode === CONFIRM_ARRIVAL_UNIT_MODE.HANDLING || anySecondaryOpen;

  const locationOptions: SelectOption[] = useMemo(
    () => context.receiveLocations.map((location) => ({ value: location.locationId, label: location.name })),
    [context.receiveLocations]
  );

  const rows = useMemo(() => buildConvoyTableRows(context), [context]);

  const columns = useMemo(
    () =>
      getReceiveConvoyColumns(t, {
        control,
        setValue,
        unitMode,
        showLostColumn,
        isSecondaryOpen,
        onToggleSecondary: toggleSecondary,
        locationOptions,
        locationsById
      }),
    [t, control, setValue, unitMode, showLostColumn, isSecondaryOpen, toggleSecondary, locationOptions, locationsById]
  );

  const renderGroupRow = (row: ReceiveConvoyTableRow) =>
    row.kind === 'truck' ? (
      <ReceiveConvoyTruckHeader shipment={row.shipment} />
    ) : (
      <ReceiveConvoyTruckDocuments form={form} shipment={row.shipment} shipmentIndex={row.shipmentIndex} />
    );

  return (
    <div className="space-y-3">
      <ConfirmArrivalUnitToolbar
        showUnitSwitch={anyBundleLines}
        unitMode={unitMode}
        onUnitModeChange={onUnitModeChange}
        onResetQuantities={onResetQuantities}
        showsHandlingFigures={showsHandlingFigures}
      />

      <ReceiveConvoyHandlingSummary form={form} context={context} />

      <DataTable
        data={rows}
        columns={columns}
        tableId="receive-convoy-lines"
        showToolbar={false}
        enableColumnCustomization={false}
        enableFullscreen={false}
        height="h-auto"
        contentClassName="px-0"
        isGroupRow={isGroupRow}
        renderGroupRow={renderGroupRow}
        rowClassName={rowClassName}
      />
    </div>
  );
}
